'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Drawer } from 'antd';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineUserPlus,
} from 'react-icons/hi2';

import ThemedButton from '../../common/buttons/Button';
import UserMenu from '../UserMenu';
import styles from './mobilemenu.module.scss';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { fetchProductMenu } from '@/redux/products/thunk';
import { MenuItem } from '@/redux/products/types';
import { useAuthGuard } from '@/hooks/useAuthGuard';

import type { HeaderLink } from './types';

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  servicesOpen: boolean;
  toggleServices: () => void;
  isLoggedIn: boolean;
  username: string;
  handleLogout: () => void;
  logo: string;
  name: string;
  links?: HeaderLink[];
}

const isAbsoluteUrl = (href?: string) => !!href && /^https?:\/\//i.test(href);

const MobileMenu: React.FC<MobileMenuProps> = ({
  open,
  onClose,
  servicesOpen,
  toggleServices,
  isLoggedIn,
  handleLogout: _handleLogout,
  logo,
  name,
  links,
}) => {
  const dispatch = useAppDispatch();
  const {
    data: menuItems,
    status: menuStatus,
    error: menuError,
  } = useAppSelector((state) => state.products.menu);

  const { guard } = useAuthGuard({
    loginPath: '/account/signin',
    onRedirect: onClose,
  });

  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (menuStatus === 'idle') {
      dispatch(fetchProductMenu({}));
    }
  }, [dispatch, menuStatus]);

  const handleSubClick =
    (section: MenuItem, sub: MenuItem) =>
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const href = `/services/application/${section.id}/i/${section.id}/${sub.id}`;
      if (guard(!!sub.requires_auth, href)) {
        e.preventDefault();
        return;
      }
      onClose();
    };

  const toggleDropdown = (key: string) =>
    setOpenDropdowns((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Drawer
      className={styles.customDrawer}
      placement="left"
      closable
      onClose={onClose}
      open={open}
      width={340}
      title={
        <div className="flex items-center">
          <Link href="/" aria-label="Home" onClick={onClose} className="block">
            <Image
              src={logo}
              alt={name}
              width={148}
              height={60}
              className="h-15 w-auto object-contain"
              priority
            />
          </Link>
        </div>
      }
      styles={{
        body: {
          padding: 0,
          backgroundColor: 'var(--color-surface-header)',
        },
        header: { backgroundColor: 'var(--color-surface-header)' },
        footer: { backgroundColor: 'var(--color-surface-header)' },
      }}
    >
      <div className={styles.authBlock}>
        {isLoggedIn ? (
          <UserMenu />
        ) : (
          <>
            <ThemedButton
              variant="outlined"
              theme="accent"
              text="Log In"
              href="/account/signin"
              icon={<HiOutlineArrowRightOnRectangle />}
            />
            <ThemedButton
              theme="primary"
              text="Sign Up"
              href="/account/signup"
              icon={<HiOutlineUserPlus />}
            />
          </>
        )}
      </div>

      <nav className={styles.nav} aria-label="Mobile navigation">
        <span className={styles.sectionTitle}>Navigation</span>

        {Array.isArray(links) && links.length > 0 ? (
          <ul>
            {links.map((item, i) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;

              if (item.kind === 'services') {
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={toggleServices}
                      className={styles.row}
                      aria-expanded={servicesOpen}
                    >
                      <span>{item.title}</span>
                      {servicesOpen ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                    </button>

                    {servicesOpen && (
                      <div>
                        {menuStatus === 'loading' ? (
                          <span className={styles.statusText}>Loading services…</span>
                        ) : menuStatus === 'failed' ? (
                          <span className={`${styles.statusText} ${styles.statusError}`}>
                            {menuError || 'Failed to load services'}
                          </span>
                        ) : menuItems.length === 0 ? (
                          <span className={styles.statusText}>No services available.</span>
                        ) : (
                          menuItems.map((section: MenuItem) => (
                            <div key={section.id} className={styles.servicesGroup}>
                              <h5>{section.name}</h5>
                              <ul>
                                {section.subproducts.map((sub) => (
                                  <li key={sub.id}>
                                    <Link
                                      href={`/services/application/${section.id}/i/${section.id}/${sub.id}`}
                                      onClick={handleSubClick(section, sub)}
                                      className={styles.subItem}
                                    >
                                      {sub.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </li>
                );
              }

              if (hasChildren) {
                const key = `dd-${i}`;
                const isOpen = !!openDropdowns[key];

                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => toggleDropdown(key)}
                      className={styles.row}
                      aria-expanded={isOpen}
                    >
                      <span>{item.title}</span>
                      {isOpen ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                    </button>

                    {isOpen && (
                      <ul className={styles.subList}>
                        {item.children!.map((child, ci) => {
                          const href = child.url || '#';
                          const external = child.external || isAbsoluteUrl(href);

                          return (
                            <li key={ci}>
                              {external ? (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={onClose}
                                  className={styles.subItem}
                                >
                                  {child.title}
                                </a>
                              ) : (
                                <Link href={href} onClick={onClose} className={styles.subItem}>
                                  {child.title}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              const href = item.url || '#';
              const external = item.external || isAbsoluteUrl(href);

              return (
                <li key={i}>
                  {external ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClose}
                      className={styles.row}
                    >
                      {item.title}
                    </a>
                  ) : (
                    <Link href={href} onClick={onClose} className={styles.row}>
                      {item.title}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <ul>
            <li>
              <Link href="/" onClick={onClose} className={styles.row}>Home</Link>
            </li>
            <li>
              <Link href="/support" onClick={onClose} className={styles.row}>Help & Support</Link>
            </li>
            <li>
              <Link href="/contact" onClick={onClose} className={styles.row}>Contact</Link>
            </li>
            <li>
              <Link href="/e-verify/documents" onClick={onClose} className={styles.row}>E-Verify</Link>
            </li>
          </ul>
        )}
      </nav>
    </Drawer>
  );
};

export default MobileMenu;
